<!--
  根组件

  职责：
  1. 提供 Naive UI 全局配置（locale、日期、主题色、暗/亮主题切换）
  2. 全局消息 / 对话框 Provider
  3. 挂载路由视图与工程加载遮罩
-->
<script setup lang="ts">
import { computed } from 'vue'
import type { GlobalThemeOverrides } from 'naive-ui'
import { NConfigProvider, NDialogProvider, NMessageProvider, darkTheme } from 'naive-ui'
import { zhCN, dateZhCN } from 'naive-ui'
import { RouterView } from 'vue-router'
import ProjectLoadOverlay from '@/components/ProjectLoadOverlay.vue'
import { useThemeStore } from '@/store/theme'

const themeStore = useThemeStore()

/** 根据当前主题决定 Naive UI 的 theme 参数：dark 用 darkTheme，light 用 null（默认亮色） */
const naiveTheme = computed(() => (themeStore.mode === 'dark' ? darkTheme : null))

const LIGHT_THEME_OVERRIDES: GlobalThemeOverrides = {
  common: {
    primaryColor: '#c26b19',
    primaryColorHover: '#a9580f',
    primaryColorPressed: '#a9580f',
    primaryColorSuppl: '#c26b19',
    infoColor: '#0078d4',
    successColor: '#388a34',
    warningColor: '#b89500',
    errorColor: '#c74e39',
    bodyColor: '#f3f3f3',
    cardColor: '#ffffff',
    modalColor: '#ffffff',
    popoverColor: '#ffffff',
    tableColor: '#ffffff',
    borderColor: '#d4d4d4',
    dividerColor: '#d4d4d4',
    inputColor: '#ffffff',
    actionColor: '#f8f8f8',
    hoverColor: '#ececec',
    textColorBase: '#333333',
    textColor1: '#202020',
    textColor2: '#333333',
    textColor3: '#4f4f4f',
    placeholderColor: '#767676',
  },
  Button: {
    textColorPrimary: '#ffffff',
    textColorHoverPrimary: '#ffffff',
    textColorPressedPrimary: '#ffffff',
    colorHoverPrimary: '#a9580f',
    colorPressedPrimary: '#a9580f',
    borderHoverPrimary: '#a9580f',
    borderPressedPrimary: '#a9580f',
  },
  Input: {
    color: '#ffffff',
    colorFocus: '#ffffff',
    borderHover: '#c8c8c8',
    borderFocus: '#0078d4',
    boxShadowFocus: 'none',
  },
  Select: {
    peers: {
      InternalSelection: {
        color: '#ffffff',
        colorActive: '#ffffff',
        borderHover: '#c8c8c8',
        borderActive: '#0078d4',
        boxShadowActive: 'none',
      },
    },
  },
  Tabs: {
    tabColorSegment: '#f3f3f3',
    panePaddingSmall: '8px 0 0',
  },
  Card: {
    color: '#ffffff',
    borderColor: '#d4d4d4',
    titleTextColor: '#202020',
  },
  Modal: {
    color: '#ffffff',
  },
}

const DARK_THEME_OVERRIDES: GlobalThemeOverrides = {
  common: {
    primaryColor: '#d18616',
    primaryColorHover: '#e59c34',
    primaryColorPressed: '#e59c34',
    primaryColorSuppl: '#d18616',
    infoColor: '#3794ff',
    successColor: '#4ec9b0',
    warningColor: '#cca700',
    errorColor: '#f14c4c',
    bodyColor: '#181818',
    cardColor: '#252526',
    modalColor: '#252526',
    popoverColor: '#252526',
    tableColor: '#252526',
    borderColor: '#313131',
    dividerColor: '#313131',
    inputColor: '#1f1f1f',
    actionColor: '#2d2d2d',
    hoverColor: '#2a2d2e',
    textColorBase: '#c8c8c8',
    textColor1: '#cccccc',
    textColor2: '#c8c8c8',
    textColor3: '#a9a9a9',
    placeholderColor: '#858585',
  },
  Button: {
    textColorPrimary: '#111111',
    textColorHoverPrimary: '#111111',
    textColorPressedPrimary: '#111111',
    colorHoverPrimary: '#e59c34',
    colorPressedPrimary: '#e59c34',
    borderHoverPrimary: '#e59c34',
    borderPressedPrimary: '#e59c34',
  },
  Input: {
    color: '#1f1f1f',
    colorFocus: '#1f1f1f',
    borderHover: '#3c3c3c',
    borderFocus: '#3794ff',
    boxShadowFocus: 'none',
  },
  Select: {
    peers: {
      InternalSelection: {
        color: '#1f1f1f',
        colorActive: '#1f1f1f',
        borderHover: '#3c3c3c',
        borderActive: '#3794ff',
        boxShadowActive: 'none',
      },
    },
  },
  Tabs: {
    tabColorSegment: '#2d2d2d',
    panePaddingSmall: '8px 0 0',
  },
  Card: {
    color: '#252526',
    borderColor: '#313131',
    titleTextColor: '#cccccc',
  },
  Modal: {
    color: '#252526',
  },
}

const themeOverrides = computed<GlobalThemeOverrides>(() =>
  themeStore.mode === 'dark' ? DARK_THEME_OVERRIDES : LIGHT_THEME_OVERRIDES,
)
</script>

<template>
  <n-config-provider
    :locale="zhCN"
    :date-locale="dateZhCN"
    :theme="naiveTheme"
    :theme-overrides="themeOverrides"
  >
    <n-message-provider>
      <n-dialog-provider>
        <RouterView />
        <ProjectLoadOverlay />
      </n-dialog-provider>
    </n-message-provider>
  </n-config-provider>
</template>

<style>
body {
  margin: 0;
}

* {
  box-sizing: border-box;
}
</style>
